import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

// Group streams carry invalidations only; every refreshed report is authorized by HTTP.
export default class extends Controller {
  static values = { url: String, activityId: String, groupId: String }

  connect() {
    this.abort = new AbortController()
    this.consumer = createConsumer()
    this.consumer.subscriptions.create({ channel: "GroupWorkshopChannel", activity_id: this.activityIdValue, group_id: this.groupIdValue }, {
      received: () => this.refresh()
    })
    this.element.querySelectorAll("[data-insight-region]").forEach(region => { region.dataset.serverHTML = region.innerHTML })
    this.timer = setInterval(() => this.refresh(), 5000)
  }

  disconnect() {
    this.abort.abort()
    this.consumer.disconnect()
    clearInterval(this.timer)
  }

  async refresh() {
    if (this.loading || this.abort.signal.aborted) return
    this.loading = true
    try {
      const response = await fetch(this.urlValue, { signal: this.abort.signal, headers: { Accept: "application/json" } })
      if (!response.ok) {
        if ([401, 403, 404].includes(response.status)) {
          this.element.replaceChildren()
          const notice = document.createElement("p")
          notice.className = "p-5 text-sm text-gray-600"
          notice.textContent = "Workshop access is no longer available."
          this.element.append(notice)
          this.disconnect()
        }
        return
      }
      const { html } = await response.json()
      if (this.abort.signal.aborted) return
      const fragment = document.createElement("template")
      fragment.innerHTML = html
      // Keep the same layout, selected tab, scroll positions and name toggles mounted.
      for (const region of this.element.querySelectorAll("[data-insight-region]")) {
        const next = fragment.content.querySelector(`[data-insight-region="${region.dataset.insightRegion}"]`)
        if (!next || region.dataset.serverHTML === next.innerHTML) continue
        if (region.contains(document.activeElement) || region.querySelector("[data-insights-skeleton], button:disabled[data-insights-generator-target], button:disabled[data-individual-insights-generator-target]")) continue
        const parentScroll = region.parentElement.scrollTop
        const checked = new Map(Array.from(region.querySelectorAll('input[type="checkbox"]'), input => [input.name, input.checked]))
        for (const input of next.querySelectorAll('input[type="checkbox"]')) {
          if (checked.has(input.name)) input.checked = checked.get(input.name)
        }
        region.dataset.serverHTML = next.innerHTML
        region.replaceChildren(...next.childNodes)
        region.parentElement.scrollTop = parentScroll
        for (const name of ["insights-sidebar", "participants-toggle"]) {
          const controller = this.application.getControllerForElementAndIdentifier(region, name)
          controller?.updateParticipantDisplay?.()
          controller?.updateDisplay?.()
        }
      }
    } catch (error) {
      if (error.name !== "AbortError") this.element.dataset.refreshPending = "true"
    } finally { this.loading = false }
  }
}
